(() => {
  const WC_LOCATOR_VERSION = "map-popup-links-2026-10-08";
  const DEFAULT_CENTER = [-98.5795, 39.8283];
  const DEFAULT_ZOOM = 3.15;

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function milesBetween(lat1, lon1, lat2, lon2) {
    const R = 3958.7613;
    const toRad = n => n * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function phoneHref(phone) {
    return "tel:" + String(phone || "").replace(/[^\d+]/g, "");
  }

  function normalizePriority(value) {
    const raw = String(value || "Standard").trim().toLowerCase();
    if (["featured", "feature", "features"].includes(raw)) return "Featured";
    if (raw === "preferred") return "Preferred";
    return "Standard";
  }

  function priorityRank(dealer) {
    const priority = normalizePriority(dealer.priority);
    if (priority === "Featured") return 0;
    if (priority === "Preferred") return 1;
    return 2;
  }

  function priorityColor(priority) {
    const normalized = normalizePriority(priority);
    if (normalized === "Featured") return "#B08A4A";
    if (normalized === "Preferred") return "#806F5C";
    return "#4A4A4A";
  }

  function directionsHref(dealer) {
    const destination = encodeURIComponent(
      `${dealer.address}, ${dealer.city}, ${dealer.state} ${dealer.zip}`
    );
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
  }

  function safeWebsite(url) {
    try {
      const parsed = new URL(url);
      return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : "#";
    } catch {
      return "#";
    }
  }

  class WyndhamDealerLocator {
    constructor(root) {
      this.root = root;
      this.config = {
        mapboxToken: root.dataset.mapboxToken || "",
        dealerApiUrl: root.dataset.dealerApiUrl || ""
      };

      this.state = {
        map: null,
        markers: [],
        userPoint: null,
        results: [],
        dealers: [],
        searched: false
      };

      this.els = {
        query: root.querySelector('[data-wc-location-query]'),
        radius: root.querySelector('[data-wc-radius]'),
        category: root.querySelector('[data-wc-category]'),
        search: root.querySelector('[data-wc-search-button]'),
        results: root.querySelector('[data-wc-results]'),
        resultCount: root.querySelector('[data-wc-result-count]'),
        status: root.querySelector('[data-wc-search-status]'),
        map: root.querySelector('[data-wc-map]')
      };

      this.bindEvents();
      this.initMap();
      this.loadDealers()
        .then(dealers => {
          this.showNationwideDealers(dealers);
          this.els.status.textContent =
            `${dealers.length} active dealer${dealers.length === 1 ? "" : "s"} shown nationwide. Enter a ZIP code or city to find dealers near you.`;
        })
        .catch(error => {
          this.els.status.textContent = error.message;
        });
    }

    async loadDealers() {
      if (!this.config.dealerApiUrl) {
        throw new Error("Dealer API URL is missing in the Shopify section settings.");
      }

      const separator = this.config.dealerApiUrl.includes("?") ? "&" : "?";
      const liveUrl = `${this.config.dealerApiUrl}${separator}_=${Date.now()}`;
      const response = await fetch(liveUrl, { cache: "no-store" });
      if (!response.ok) throw new Error("Dealer database could not be loaded.");

      const data = await response.json();
      if (!data.ok) throw new Error(data.error || "Dealer API returned an error.");

      this.state.dealers = (Array.isArray(data.dealers) ? data.dealers : [])
        .map(dealer => ({
          ...dealer,
          priority: normalizePriority(dealer.priority),
          lat: Number(dealer.lat),
          lng: Number(dealer.lng)
        }))
        .filter(dealer => dealer.name && Number.isFinite(dealer.lat) && Number.isFinite(dealer.lng));

      return this.state.dealers;
    }

    rankDealers(userLat, userLng, radius, category) {
      return this.state.dealers
        .map(dealer => ({
          ...dealer,
          distance: milesBetween(userLat, userLng, dealer.lat, dealer.lng)
        }))
        .filter(dealer => Number.isFinite(dealer.distance) && dealer.distance <= radius)
        .filter(dealer => category === "All" || (dealer.categories || []).includes(category))
        .sort((a, b) => {
          const rankDifference = priorityRank(a) - priorityRank(b);
          if (rankDifference !== 0) return rankDifference;
          return a.distance - b.distance;
        });
    }

    async geocodeLocation(query) {
      if (!this.config.mapboxToken) {
        throw new Error("Mapbox public token is missing in the Shopify section settings.");
      }

      const url = new URL("https://api.mapbox.com/search/geocode/v6/forward");
      url.searchParams.set("q", query);
      url.searchParams.set("country", "US");
      url.searchParams.set("limit", "1");
      url.searchParams.set("access_token", this.config.mapboxToken);

      const response = await fetch(url);
      if (!response.ok) throw new Error("Location search failed.");

      const data = await response.json();
      const feature = data.features?.[0];
      if (!feature) throw new Error("We couldn't find that ZIP code or city.");

      const [lng, lat] = feature.geometry.coordinates;
      return {
        lng,
        lat,
        label: feature.properties?.full_address || feature.properties?.name || query
      };
    }

    initMap() {
      if (!window.mapboxgl || !this.els.map) return;

      if (!this.config.mapboxToken) {
        this.els.map.innerHTML =
          '<div class="wc-dealer-locator__empty-state"><strong>Map setup needed.</strong><br>Add your Mapbox public token in the section settings.</div>';
        return;
      }

      mapboxgl.accessToken = this.config.mapboxToken;
      this.state.map = new mapboxgl.Map({
        container: this.els.map,
        style: "mapbox://styles/mapbox/light-v11",
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM
      });

      this.state.map.addControl(new mapboxgl.NavigationControl(), "top-right");
    }

    clearMarkers() {
      this.state.markers.forEach(marker => marker.remove());
      this.state.markers = [];
    }

    buildMarkerElement(priority, compact = false) {
      const el = document.createElement("button");
      el.type = "button";
      el.className = `wc-dealer-locator__map-pin${compact ? " is-compact" : ""}`;
      el.style.setProperty("--pin-color", priorityColor(priority));
      el.setAttribute("aria-label", `${normalizePriority(priority)} dealer location`);
      return el;
    }

    markerPopupHtml(dealer, includeDistance = false) {
      const priority = normalizePriority(dealer.priority);
      const priorityLabel = priority === "Standard" ? "" :
        `<div class="wc-map-popup__priority wc-map-popup__priority--${priority.toLowerCase()}">${priority} Dealer</div>`;
      const distance = includeDistance && Number.isFinite(dealer.distance)
        ? `<div class="wc-map-popup__distance">${dealer.distance.toFixed(1)} miles away</div>`
        : "";
      const locality = [dealer.city, dealer.state, dealer.zip].filter(Boolean).join(" ");
      const addressLine = [dealer.address, locality].filter(Boolean).join(", ");
      const phoneLink = dealer.phone
        ? `<a class="wc-map-popup__phone" href="${phoneHref(dealer.phone)}">${escapeHtml(dealer.phone)}</a>`
        : "";
      const directionsLink = `<a class="wc-map-popup__directions" href="${directionsHref(dealer)}" target="_blank" rel="noopener">Directions</a>`;
      const websiteLink = dealer.website && safeWebsite(dealer.website) !== "#"
        ? `<a class="wc-map-popup__website" href="${escapeHtml(safeWebsite(dealer.website))}" target="_blank" rel="noopener">Website</a>`
        : "";

      return `<div class="wc-map-popup">
        <strong>${escapeHtml(dealer.name)}</strong>
        ${priorityLabel}
        ${distance}
        ${addressLine ? `<div class="wc-map-popup__address">${escapeHtml(addressLine)}</div>` : ""}
        ${phoneLink ? `<div class="wc-map-popup__contact">${phoneLink}</div>` : ""}
        <div class="wc-map-popup__actions">${directionsLink}${websiteLink}</div>
      </div>`;
    }

    showNationwideDealers(dealers) {
      if (!this.state.map) return;
      this.clearMarkers();

      dealers.forEach(dealer => {
        const marker = new mapboxgl.Marker({
          element: this.buildMarkerElement(dealer.priority, true),
          anchor: "center"
        })
          .setLngLat([dealer.lng, dealer.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 14 }).setHTML(this.markerPopupHtml(dealer, false))
          )
          .addTo(this.state.map);

        this.state.markers.push(marker);
      });

      this.state.map.jumpTo({ center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    }

    addMapResults(results, userPoint) {
      if (!this.state.map) return;

      this.clearMarkers();
      const bounds = new mapboxgl.LngLatBounds();

      results.forEach(dealer => {
        const marker = new mapboxgl.Marker({
          element: this.buildMarkerElement(dealer.priority, false),
          anchor: "bottom"
        })
          .setLngLat([dealer.lng, dealer.lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 22 }).setHTML(this.markerPopupHtml(dealer, true))
          )
          .addTo(this.state.map);

        this.state.markers.push(marker);
        bounds.extend([dealer.lng, dealer.lat]);
      });

      if (userPoint) bounds.extend([userPoint.lng, userPoint.lat]);

      if (results.length) {
        this.state.map.fitBounds(bounds, {
          padding: 60,
          maxZoom: 12,
          duration: 700
        });
      } else if (userPoint) {
        this.state.map.flyTo({ center: [userPoint.lng, userPoint.lat], zoom: 9 });
      }
    }

    renderResults(results) {
      this.els.resultCount.textContent =
        `${results.length} dealer${results.length === 1 ? "" : "s"} found`;

      if (!results.length) {
        this.els.results.innerHTML = `
          <div class="wc-dealer-locator__empty-state">
            No dealers were found using those filters. Try a larger search radius or select All Products.
          </div>`;
        return;
      }

      this.els.results.innerHTML = results.map(dealer => {
        const priority = normalizePriority(dealer.priority);
        const isFeatured = priority === "Featured";
        const isPreferred = priority === "Preferred";
        const cardClass = isFeatured ? "is-featured" : isPreferred ? "is-preferred" : "";
        return `
        <article class="wc-dealer-locator__card ${cardClass}">
          ${isFeatured ? '<div class="wc-dealer-locator__badge wc-dealer-locator__badge--featured">Featured Dealer</div>' : ""}
          ${isPreferred ? '<div class="wc-dealer-locator__badge wc-dealer-locator__badge--preferred">Preferred Dealer</div>' : ""}
          <h2>${escapeHtml(dealer.name)}</h2>
          <div class="wc-dealer-locator__distance">${dealer.distance.toFixed(1)} miles away</div>

          <div class="wc-dealer-locator__address">
            ${escapeHtml(dealer.address)}<br>
            ${escapeHtml(dealer.city)}, ${escapeHtml(dealer.state)} ${escapeHtml(dealer.zip)}
          </div>

          ${dealer.phone ? `<div class="wc-dealer-locator__phone">${escapeHtml(dealer.phone)}</div>` : ""}

          <div class="wc-dealer-locator__categories">
            ${(dealer.categories || []).length ? `<strong>Available:</strong> ${(dealer.categories || []).map(escapeHtml).join(" · ")}` : ""}
            ${dealer.showroom ? "<br><strong>Showroom:</strong> Yes" : ""}
          </div>

          <div class="wc-dealer-locator__actions">
            ${dealer.phone ? `<a href="${phoneHref(dealer.phone)}">Call</a>` : ""}
            <a href="${directionsHref(dealer)}" target="_blank" rel="noopener">Directions</a>
            ${dealer.website ? `<a class="is-primary" href="${escapeHtml(safeWebsite(dealer.website))}" target="_blank" rel="noopener">Visit Website</a>` : ""}
          </div>

          ${isFeatured ? '<div class="wc-dealer-locator__on-display">On Display</div>' : ""}
        </article>`;
      }).join("");
    }

    async search() {
      const query = this.els.query.value.trim();

      if (!query) {
        this.state.searched = false;
        this.els.status.textContent = "Enter a ZIP code or city.";
        this.els.resultCount.textContent = "";
        this.els.results.innerHTML = `
          <div class="wc-dealer-locator__empty-state">
            Enter a ZIP code or city to find Wyndham Collection dealers near you.
          </div>`;
        this.showNationwideDealers(this.state.dealers);
        return;
      }

      this.els.search.disabled = true;
      this.els.search.textContent = "Searching…";
      this.els.status.textContent = "Finding dealers…";

      try {
        // Re-fetch on every search so Google Sheet edits are reflected without a page refresh.
        await this.loadDealers();

        const point = await this.geocodeLocation(query);
        const radius = Number(this.els.radius.value);
        const category = this.els.category.value;

        this.state.searched = true;
        this.state.userPoint = point;
        this.state.results = this.rankDealers(point.lat, point.lng, radius, category);

        this.renderResults(this.state.results);
        this.addMapResults(this.state.results, point);

        this.els.status.textContent = `Results near ${point.label}`;
      } catch (error) {
        this.els.status.textContent = error.message;
        this.els.resultCount.textContent = "";
      } finally {
        this.els.search.disabled = false;
        this.els.search.textContent = "Find Dealers";
      }
    }

    bindEvents() {
      this.els.search.addEventListener("click", () => this.search());
      this.els.query.addEventListener("keydown", event => {
        if (event.key === "Enter") this.search();
      });
      this.els.radius.addEventListener("change", () => {
        if (this.state.userPoint && this.state.searched) this.search();
      });
      this.els.category.addEventListener("change", () => {
        if (this.state.userPoint && this.state.searched) this.search();
      });
    }
  }

  function initLocators(scope = document) {
    scope.querySelectorAll('[data-wc-dealer-locator]:not([data-wc-initialized])').forEach(root => {
      root.setAttribute('data-wc-initialized', 'true');
      new WyndhamDealerLocator(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initLocators());
  } else {
    initLocators();
  }

  document.addEventListener("shopify:section:load", event => initLocators(event.target));
})();
